import { useCallback, useMemo } from 'react';
import find from 'lodash/find';
import isArray from 'lodash/isArray';

import {
  MultiSelectChangeCallbackType,
  MultiSelectDataType
} from '../../../../../../helpers/MultiSelect/types';
import {
  MultiSelect,
  MultiSelectProps
} from '../../../../../../helpers/MultiSelect';

import { useRoles } from '../../../../hooks/useRoles';
import {
  FetchRolesQueryResponse,
  FETCH_ROLES_QUERY
} from '../../../../queries/fetchRoles.query';
import { RoleCache } from '../../../../RoleCache';
import { prettyRoleName } from '../../../../utils/prettyRoleName';
import { RoleID } from '../../../../rolesTypes';
import { financeRoleIds } from '../../../../rolesConstants';

const financeRolesInitialFilters = { id: { in: financeRoleIds } };

type RolesMultiSelectDataType = MultiSelectDataType[];

interface RolesMultiSelectFieldControlProps {
  financeRoles?: boolean;
  value: RoleID[];
  onChange: (value: RoleID[]) => void;
}

function RolesMultiSelectFieldControl({
  financeRoles,
  closeMenuOnSelect = true,
  defaultValue = null,
  disabled = false,
  emptyValue = null,
  i18nLabel = null,
  i18nPlaceholder,
  inputWrapperClassName = null,
  isClearable = false,
  labelClassName,
  menuIsOpen,
  menuPlacement = 'auto',
  menuPosition,
  name,
  placeholder = null,
  error,
  value,
  onChange
}: RolesMultiSelectFieldControlProps & MultiSelectProps) {
  const { roles, rolesLoading, rolesErrorMessage } =
    useRoles<FetchRolesQueryResponse>({
      query: FETCH_ROLES_QUERY,
      cacheKey: financeRoles
        ? RoleCache.financeRolesCacheKey()
        : RoleCache.indexCacheKey(),
      initialFilters: financeRoles ? financeRolesInitialFilters : undefined
    });

  const rolesData = useMemo<RolesMultiSelectDataType>(() => {
    return roles.map((role) => ({
      value: role.id,
      label: prettyRoleName(role.name)
    }));
  }, [roles]);

  const handleChange = useCallback<MultiSelectChangeCallbackType>(
    (value: MultiSelectDataType) => {
      onChange(isArray(value) ? value.map((item) => item.value) : []);
    },
    [onChange]
  );

  return (
    <MultiSelect
      closeMenuOnSelect={closeMenuOnSelect}
      defaultValue={defaultValue}
      disabled={disabled}
      emptyValue={emptyValue}
      error={error || rolesErrorMessage}
      i18nLabel={i18nLabel}
      i18nPlaceholder={i18nPlaceholder}
      inputWrapperClassName={inputWrapperClassName}
      isClearable={isClearable}
      labelClassName={labelClassName}
      menuIsOpen={menuIsOpen}
      menuPlacement={menuPlacement}
      menuPosition={menuPosition}
      onChange={handleChange}
      placeholder={placeholder}
      value={
        isArray(value)
          ? value.map((item) => find(rolesData, ['value', item]))
          : []
      }
      name={name}
      multi={true}
      isSearchable={true}
      optionsLoading={rolesLoading}
      data={rolesData}
      classNamePrefix="av"
    />
  );
}

export default RolesMultiSelectFieldControl;
